import { useRef, type ReactNode } from 'react'
import { animate, motion, useMotionValue, useTransform, type PanInfo } from 'framer-motion'
import { CheckCheck, Mail, Trash2 } from 'lucide-react'
import { usePrefersReducedMotion } from '@/hooks/useMediaQuery'

/** Past this (px) a release toggles read; delete needs this or a fling. */
const ACTION_PX = 72
const FLING_VELOCITY = 650
const SNAP_BACK = { type: 'spring', stiffness: 600, damping: 42 } as const

interface NotificationSwipeRowProps {
  children: ReactNode
  isUnread: boolean
  /** Touch screens only — desktop rows use hover buttons instead. */
  enabled: boolean
  onDelete: () => void
  onToggleRead: () => void
}

/**
 * Mail-style row: swipe left → Delete (red, right side), swipe right →
 * Read / Unread (blue, left side). Each side shows only its own action.
 */
export function NotificationSwipeRow({
  children,
  isUnread,
  enabled,
  onDelete,
  onToggleRead,
}: NotificationSwipeRowProps) {
  const reducedMotion = usePrefersReducedMotion()
  const x = useMotionValue(0)
  const rowRef = useRef<HTMLDivElement>(null)
  const draggedRef = useRef(false)
  const busyRef = useRef(false)
  const readOpacity = useTransform(x, [0, 12], [0, 1])
  const deleteOpacity = useTransform(x, [-12, 0], [1, 0])
  const readScale = useTransform(x, [12, ACTION_PX], [0.7, 1])
  const deleteScale = useTransform(x, [-ACTION_PX, -12], [1, 0.7])

  if (!enabled) return <>{children}</>

  const onDragEnd = (_: unknown, info: PanInfo) => {
    // The click (if any) fires right after release; clear before the next tap.
    requestAnimationFrame(() => {
      draggedRef.current = false
    })
    if (busyRef.current) return
    const { offset, velocity } = info
    const flungLeft = offset.x < -24 && velocity.x < -FLING_VELOCITY

    if (offset.x <= -ACTION_PX || flungLeft) {
      busyRef.current = true
      const width = rowRef.current?.offsetWidth ?? 400
      let done = false
      const finish = () => {
        if (done) return
        done = true
        onDelete()
      }
      if (reducedMotion) finish()
      else {
        void animate(x, -width, { duration: 0.16, ease: 'easeOut' }).then(finish)
        // Never leave a row "gone" on screen but still in the list.
        window.setTimeout(finish, 260)
      }
      return
    }

    if (offset.x >= ACTION_PX) onToggleRead()
    void animate(x, 0, SNAP_BACK)
  }

  return (
    <div ref={rowRef} className="relative overflow-hidden">
      <motion.div
        aria-hidden
        style={{ opacity: readOpacity }}
        // Fixed blue — the theme primary is red here and would read as Delete.
        className="pointer-events-none absolute inset-0 flex items-center justify-start bg-sky-600 px-5 text-white"
      >
        <motion.span style={{ scale: readScale }} className="flex items-center gap-1.5 text-xs font-semibold">
          {isUnread ? <CheckCheck className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
          {isUnread ? 'Read' : 'Unread'}
        </motion.span>
      </motion.div>
      <motion.div
        aria-hidden
        style={{ opacity: deleteOpacity }}
        className="pointer-events-none absolute inset-0 flex items-center justify-end bg-destructive px-5 text-white"
      >
        <motion.span style={{ scale: deleteScale }} className="flex items-center gap-1.5 text-xs font-semibold">
          Delete
          <Trash2 className="h-4 w-4" />
        </motion.span>
      </motion.div>

      <motion.div
        drag="x"
        dragDirectionLock
        dragMomentum={false}
        dragConstraints={{ left: -480, right: 140 }}
        dragElastic={0.08}
        style={{ x, touchAction: 'pan-y' }}
        onDragStart={() => {
          draggedRef.current = true
        }}
        onDragEnd={onDragEnd}
        onClickCapture={(event) => {
          // A swipe is not a tap — don't open the notification.
          if (draggedRef.current) {
            event.preventDefault()
            event.stopPropagation()
          }
        }}
        className="relative bg-card"
      >
        {children}
      </motion.div>
    </div>
  )
}
